/**
 * 确认页的**纯逻辑**：字段落在哪一页、值怎么回显
 * -----------------------------------------------------------------------------
 * 与 `EnrollConfirm.tsx` 分开是为了可单测：那个文件 import 了 antd-mobile，
 * 在 vitest 的 node 环境里连 CJS 都解析不过去（`Unexpected token ':'`）。
 * 页面文件只负责画，判定全在这里。
 */
import { FieldType, type FormFieldDef, formatRegionText } from '@enroll/shared';

/**
 * 「承诺」与「签名」这两类字段固定住在确认页，**不跟后台分组走**。
 *
 * 判定按类型 + 是否在 `extra` 组：
 *   - `signature` 型只有一个用途（手写签名承诺），全走确认页；
 *   - `checkbox` 型只有 `extra` 组里的那个才是「承诺条款」（报考条件确认），
 *     教育组里的复选框属于报考信息，仍留在第 1 步。
 * 用类型而不是写死 key：后台把字段 rename 掉也不会让它突然跑回第 1 步。
 *
 * 放在共享的纯逻辑里而不是页面内：第 1 步要据此把字段排掉、确认页要据此取字段，
 * 两处各写一遍必然漂移（一处按类型、一处按 key 的那种）。
 */
export const PROMISE_GROUP = 'extra';

export function isPromiseField(field: FormFieldDef): boolean {
  return field.type === FieldType.CHECKBOX && field.group === PROMISE_GROUP;
}

export function isSignatureField(field: FormFieldDef): boolean {
  return field.type === FieldType.SIGNATURE;
}

/**
 * 「职业证件照」插进汇总时挂在哪个分组下（后台 seed 里 `basic` = 「基本信息」）。
 *
 * 它不是表单字段 —— 由 ③ 在线生成、挂在 `Asset` 上，表单答案里从来没有这一项。
 * 放进「基本信息」是产品口径：它和姓名 / 证件号 / 证件类型都是 ② 那张身份证卡
 * 产出的，照片紧挨着名字，一眼能看出是不是本人。
 */
export const PHOTO_GROUP_KEY = 'basic';

/**
 * 照片行该插在第几张卡里。
 *
 * 找不到目标分组时退到**第一组**，而不是返回 -1 让调用方不渲染 —— 一张照片
 * 无声消失，学员会以为这次没生成，然后回去重拍一遍（还要占用重拍次数）。
 * 空数组也返回 0：`map` 本来就不会渲染，给个合法下标比给 undefined 好推理。
 */
export function photoRowGroupIndex(groups: readonly { key: string }[]): number {
  const found = groups.findIndex((group) => group.key === PHOTO_GROUP_KEY);
  return found >= 0 ? found : 0;
}

/**
 * 字段值 → 只读文案。
 *
 * 与后台表格的 `formatAnswer`（`admin/pages/Registration/query.ts`）刻意分开：
 * 那份空值显示「—」（表格里要紧凑），这份显示「未填写」（学员要看出**漏了什么**）。
 */
export function formatAnswerText(field: FormFieldDef, value: unknown): string {
  if (value === undefined || value === null || value === '') return '未填写';
  if (field.type === FieldType.REGION) return formatRegionText(value) || '未填写';
  // 上传件与签名的值是 assetId，不直接外露 —— 学员只需要知道「交没交」
  if (field.type === FieldType.UPLOAD) {
    // 多份字段（D-06）的值是数组：空数组要说「未填写」，而不是「已上传 0 份」
    if (Array.isArray(value)) {
      const count = value.filter((item) => typeof item === 'string' && item.trim() !== '').length;
      if (count === 0) return '未填写';
      return count > 1 ? `已上传 ${count} 份` : '已上传';
    }
    return '已上传';
  }
  if (field.type === FieldType.SIGNATURE) return '已签名';
  if (typeof value === 'boolean') return value ? '是' : '否';
  const labelOf = (item: unknown): string => {
    const option = field.options?.find((opt) => opt.value === item);
    return option ? option.label : String(item);
  };
  if (Array.isArray(value)) return value.map(labelOf).join('、');
  if (field.type === FieldType.SELECT || field.type === FieldType.RADIO) return labelOf(value);
  return String(value);
}
